'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import { X, ArrowLeft, ArrowRight, Expand } from 'lucide-react';
import { PostUpscaleData } from '../post/imageComponentAction';
import { setMainUpscale } from '@/lib/api/upscale';
import { useRouter } from 'next/navigation';
import { revalidatePostCache } from '@/lib/actions/upscale';
import Leaflet from '../shared/leaflet';

// Extended interface to include original image
interface ImageData extends PostUpscaleData {
  isOriginal?: boolean;
  displayed?: boolean;
}

// Fullscreen View Component
const FullscreenImageView: React.FC<{
  images: ImageData[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (newIndex: number) => void;
  onSetMain: (id: string, upscaleUrl?: string, upscaleFactor?: number) => Promise<void>;
  translations: any;
  mainImageId: string | null;
}> = ({ images, currentIndex, onClose, onNavigate, onSetMain, translations, mainImageId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const image = images[currentIndex];

  const handlePrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentIndex > 0) {
      onNavigate(currentIndex - 1);
    }
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentIndex < images.length - 1) {
      onNavigate(currentIndex + 1);
    }
  };

  const handleSetMain = async () => {
    setIsLoading(true);
    try {
      await onSetMain(image.upscaleGenerationId, image.upscaleUrl, image.upscaleFactor);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="animate-fade-in fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      onClick={onClose}
    >
      {/* Left Navigation */}
      {currentIndex > 0 && (
        <button
          onClick={handlePrevious}
          className="absolute left-6 top-1/2 z-20 -translate-y-1/2 p-3 text-white transition-colors hover:text-gray-300"
          aria-label="Previous image"
        >
          <ArrowLeft size={24} />
        </button>
      )}

      {/* Main Content */}
      <div className="relative h-full w-full p-4" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute right-2 top-2 z-20 p-2 text-white transition-colors hover:text-gray-300 sm:right-4 sm:top-4"
          aria-label={translations.close}
        >
          <X size={24} />
        </button>
        <div className="flex h-full w-full flex-col items-center justify-center gap-4">
          <div className="relative h-[80%] w-full flex-grow">
            <Image
              src={image.upscaleUrl}
              alt={`${image.isOriginal ? translations.original : 'Upscaled'} image ${
                image.upscaleGenerationId
              }`}
              layout="fill"
              objectFit="contain"
            />
          </div>
          <button
            onClick={handleSetMain}
            disabled={isLoading || mainImageId === image.upscaleGenerationId}
            className={`flex-shrink-0 rounded-md bg-crp-red px-6 py-3 text-lg font-semibold text-white shadow-lg transition-transform hover:brightness-110 disabled:cursor-not-allowed disabled:bg-crp-dark-grey disabled:hover:brightness-100`}
          >
            {isLoading ? `${translations.setting}...` : `${translations.setAsPrimary}`}
          </button>
        </div>
      </div>

      {/* Right Navigation */}
      {currentIndex < images.length - 1 && (
        <button
          onClick={handleNext}
          className="absolute right-6 top-1/2 z-20 -translate-y-1/2 p-3 text-white transition-colors hover:text-gray-300"
          aria-label="Next image"
        >
          <ArrowRight size={24} />
        </button>
      )}
    </div>
  );
};

// Grid of upscaled images
const UpscaledImagesGrid: React.FC<{
  images: ImageData[];
  onImageClick: (index: number) => void;
  translations: any;
  onSetMain: (id: string, upscaleUrl?: string, upscaleFactor?: number) => Promise<void>;
}> = ({ images, onImageClick, translations, onSetMain }) => {
  if (!images || images.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-600 bg-gray-800/50 p-4 text-center sm:p-8">
        <h3 className="text-lg font-semibold text-white">{translations.noImages}</h3>
        <p className="mt-2 text-sm text-gray-400">{translations.upscalesAppearHere}</p>
      </div>
    );
  }
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState<number>(0);
  const reversedImages: ImageData[] = [...images].reverse();
  const visibleImages: ImageData[] = filter
    ? reversedImages.filter((img) => img.upscaleFactor === filter)
    : reversedImages;
  const initialMain = images.find((img) => img.displayed) ?? images[0];
  const [selectedId, setSelectedId] = useState<string | null>(initialMain.upscaleGenerationId);
  const selectedImage =
    visibleImages && visibleImages.length > 0
      ? visibleImages.find((img) => img.upscaleGenerationId === selectedId) ?? visibleImages[0]
      : reversedImages[0];

  useEffect(() => {
    if (!visibleImages.length) return;
    if (!visibleImages.some((img) => img.upscaleGenerationId === selectedId)) {
      setSelectedId(visibleImages[0].upscaleGenerationId);
    }
  }, [filter, images]);

  const versionMap = new Map(images.map((img, id) => [img.upscaleGenerationId, id]));

  // Format helper
  function formatDateTime(isoString: string) {
    const date = new Date(isoString);
    const datePart = date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric'
    });
    const timePart = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });

    return `${datePart}, ${timePart}`;
  }

  const handleSetMain = async () => {
    if (!selectedImage) return;
    setIsLoading(true);
    try {
      await onSetMain(
        selectedImage.upscaleGenerationId,
        selectedImage.upscaleUrl,
        selectedImage.upscaleFactor
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex max-h-[80vh] flex-col overflow-y-auto bg-[#2B2D31] text-white md:h-full md:overflow-visible md:rounded-md">
      <div className="flex flex-col p-4 text-white">
        <h1 className="flex-shrink-0 text-lg font-semibold sm:text-xl">
          {translations.upscaleHistory}
        </h1>
        <div className="text-sm font-medium">
          {selectedImage?.isOriginal
            ? `${translations.original}`
            : `V${versionMap.get(selectedImage?.upscaleGenerationId ?? '')}`}
        </div>
      </div>
      <div className="flex flex-col gap-4 sm:grid sm:grid-cols-2 md:px-4">
        <div>
          <div className="col-span-1 my-0 mr-0 block h-[60vh] overflow-hidden border-y-[0.5px] border-[#3C3C3C] bg-[#2B2D31] shadow-lg sm:h-[60vh] sm:rounded-md md:col-span-4 md:mr-4 md:border-[0.5px]">
            <div className="relative flex h-full w-full items-center justify-center">
              <Image
                key={selectedImage?.upscaleGenerationId}
                className="h-full w-full rounded-md object-cover"
                src={selectedImage?.upscaleUrl}
                alt={translations.aiPorn + ' ' + translations.picture}
                layout="fill"
                objectFit="contain"
                loading="lazy"
              />
              <button
                aria-label="expand image"
                onClick={() =>
                  onImageClick(
                    images.findIndex(
                      (img) => img.upscaleGenerationId === selectedImage?.upscaleGenerationId
                    )
                  )
                }
                className="absolute bottom-0 right-0"
              >
                <Image
                  src={'https://cdn6.imgpog.com/rectangle.svg'}
                  alt="expand"
                  width={40}
                  height={40}
                />
                <Expand
                  color="#2B2D31"
                  className="absolute bottom-0 right-0 h-5 w-auto pb-1 pr-1"
                />
              </button>
            </div>
          </div>
        </div>
        <div className="flex-2 flex-col border-y-[0.5px] border-grey-outline md:rounded-md md:border">
          <div className="inline-flex w-full gap-3 border-b border-grey-outline px-5 py-4">
            {[0, 4, 16].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`rounded-md border border-grey-outline px-4 py-[7px] text-white hover:brightness-110 ${
                  filter === f ? 'bg-crp-red' : 'bg-[#2B2D31]'
                }`}
              >
                {f === 0 ? `${translations.all}` : `${f}x`}
              </button>
            ))}
          </div>
          <div className="max-h-[50vh] overflow-y-auto">
            {visibleImages &&
              visibleImages.map((img) => {
                const isSelected = img.upscaleGenerationId === selectedImage?.upscaleGenerationId;
                const isMain = img.displayed === true;
                return (
                  <div
                    key={img.upscaleGenerationId}
                    onClick={() => setSelectedId(img.upscaleGenerationId)}
                    className={`flex flex-row gap-4 border-b border-grey-outline px-5 py-4 last:border-none hover:brightness-110 ${
                      isSelected ? 'bg-[#4D4F59]' : 'bg-[#2B2D31]'
                    }`}
                  >
                    <Image
                      src={img.upscaleUrl}
                      alt={`Upscaled image ${img.upscaleGenerationId}`}
                      width={50}
                      height={50}
                      className="w-12 object-contain transition-transform duration-300 ease-in-out group-hover:scale-105"
                    />
                    <div className="flex flex-col gap-[6px] text-white">
                      <div className="text-sm font-semibold">
                        {img.isOriginal
                          ? `${translations.original}`
                          : `V${versionMap.get(img.upscaleGenerationId)}`}
                        {isMain && (
                          <span className="ml-2 text-xs text-crp-red">
                            ({translations.primary})
                          </span>
                        )}
                      </div>
                      {img.isOriginal ? (
                        <div className="text-sm font-medium">--</div>
                      ) : (
                        <div className="text-sm font-medium">{formatDateTime(img.createdAt)}</div>
                      )}
                      <div className="text-xs">
                        {!img.isOriginal && `${img.width}x${img.height} •`} {translations.aiPorn}{' '}
                        {!img.isOriginal && `${img.upscaleFactor}x ${translations.upscaled} `}{' '}
                        {translations.picture}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {/* Footer action */}
      <div className="sticky bottom-0 flex w-full items-center justify-center rounded-b-md bg-[#2B2D31] px-5 py-4">
        <button
          onClick={handleSetMain}
          className="rounded-md bg-crp-red px-4 py-2 text-white hover:brightness-110 disabled:cursor-not-allowed disabled:bg-crp-dark-grey disabled:hover:brightness-100"
          disabled={selectedImage?.displayed || isLoading}
        >
          {isLoading ? `${translations.setting}...` : `${translations.setAsPrimary}`}
        </button>
      </div>
    </div>
  );
};

// Main modal component
interface UpscaledImagesModalProps {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  upscales: PostUpscaleData[];
  originalImage?: {
    id: string;
    url: string;
  };
  translations: any;
  displayedUpscaleUrl?: string;
}

const UpscaledImagesModal: React.FC<UpscaledImagesModalProps> = ({
  showModal,
  setShowModal,
  upscales,
  originalImage,
  translations,
  displayedUpscaleUrl
}) => {
  const [fullscreenImageIndex, setFullscreenImageIndex] = useState<number | null>(null);
  const [mainImageId, setMainImageId] = useState<string | null>(null);
  const [isSettingMain, setIsSettingMain] = useState(false);
  const router = useRouter();
  useEffect(() => {
    if (showModal) {
      // Create a temporary combined list of all images to find the main one.
      const allImagesForEffect: ImageData[] = [];
      if (originalImage) {
        allImagesForEffect.push({
          upscaleGenerationId: originalImage.id,
          upscaleFactor: 0,
          upscaleUrl: originalImage.url,
          createdAt: '',
          width: 0,
          height: 0,
          isOriginal: true,
          displayed: !displayedUpscaleUrl
        });
      }
      allImagesForEffect.push(...upscales.map((upscale) => ({ ...upscale, isOriginal: false })));

      let currentMainId: string | null = null;
      if (displayedUpscaleUrl) {
        const mainImage = allImagesForEffect.find((img) => img.upscaleUrl === displayedUpscaleUrl);
        if (mainImage) {
          currentMainId = mainImage.upscaleGenerationId;
        }
      } else if (originalImage) {
        // If no specific upscale URL is displayed, default to the original image.
        currentMainId = originalImage.id;
      }
      setMainImageId(currentMainId);
    }
  }, [showModal, displayedUpscaleUrl, upscales, originalImage]);

  if (!showModal) {
    return null;
  }

  // Combine original image with upscales
  const allImages: ImageData[] = [];

  // Add original image first if provided
  if (originalImage) {
    allImages.push({
      upscaleGenerationId: originalImage.id,
      upscaleFactor: 0,
      createdAt: '',
      width: 0,
      height: 0,
      upscaleUrl: originalImage.url,
      isOriginal: true,
      displayed: !displayedUpscaleUrl
    });
  }

  // Add upscaled images
  allImages.push(...upscales.map((upscale) => ({ ...upscale, isOriginal: false })));

  const handleSetMain = async (id: string, upscaleUrl?: string, upscaleFactor?: number) => {
    if (isSettingMain) return;
    setIsSettingMain(true);
    try {
      // Use the server action instead
      await setMainUpscale(id, upscaleUrl, upscaleFactor);
      const result = await revalidatePostCache(originalImage?.id || '');

      if (result.success) {
        setMainImageId(id);
        setFullscreenImageIndex(null);

        // Force a router refresh to get the new data
        router.refresh();
        setShowModal(false);
      } else {
        console.error('Error setting main upscale:', result.error);
      }
    } catch (error) {
      console.error('Error setting main upscale:', error);
    } finally {
      setIsSettingMain(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 hidden items-center justify-center bg-black/80 backdrop-blur-sm sm:flex"
        onClick={() => setShowModal(false)}
      >
        <div
          className="relative mx-4 flex max-h-[85vh] w-full max-w-4xl flex-col rounded-xl bg-[#2B2D31] shadow-2xl sm:mx-auto sm:max-h-[90vh] sm:max-w-[1000px]"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => setShowModal(false)}
            className="absolute right-1 top-1 z-10 p-1.5 text-white transition-colors hover:text-gray-300 sm:right-2 sm:top-2"
            aria-label={translations.close}
          >
            <X size={20} />
          </button>

          <UpscaledImagesGrid
            images={allImages}
            onImageClick={setFullscreenImageIndex}
            translations={translations}
            onSetMain={handleSetMain}
          />
        </div>
      </div>
      <div className="sm:hidden">
        <Leaflet setShow={setShowModal}>
          <div className="flex-1">
            <UpscaledImagesGrid
              images={allImages}
              onImageClick={setFullscreenImageIndex}
              translations={translations}
              onSetMain={handleSetMain}
            />
          </div>
        </Leaflet>
      </div>
      {fullscreenImageIndex !== null && (
        <FullscreenImageView
          images={allImages}
          currentIndex={fullscreenImageIndex}
          onClose={() => setFullscreenImageIndex(null)}
          onNavigate={setFullscreenImageIndex}
          onSetMain={handleSetMain}
          translations={translations}
          mainImageId={mainImageId}
        />
      )}
    </>
  );
};

export default UpscaledImagesModal;
